import React, { useState } from 'react';
import { Settings, Save } from 'lucide-react';
import { apiService } from '../services/api';
import { toast } from 'sonner';

interface ConfigPanelProps {
  workspace: any;
  onUpdate: () => void;
}

const ConfigPanel = ({ workspace, onUpdate }: ConfigPanelProps) => {
  const [saving, setSaving] = useState(false);
  const [config, setConfig] = useState({
    ai_provider: workspace.config?.ai_provider || 'openai',
    ai_model: workspace.config?.ai_model || '',
    framework: workspace.config?.framework || '',
    language: workspace.config?.language || '',
    deployment_target: workspace.config?.deployment_target || '',
  });

  const handleSave = async () => {
    setSaving(true);

    try {
      await apiService.updateWorkspace(workspace.id, {
        config: config,
      });
      toast.success('Configuración guardada exitosamente');
      onUpdate();
    } catch (error) {
      toast.error('Error al guardar configuración');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <div className="mb-6">
        <h2 className="text-2xl font-medium tracking-tight text-white mb-1">
          Configuración del Workspace
        </h2>
        <p className="text-[#A1A1AA] text-sm">
          Preferencias de IA, framework y despliegue
        </p>
      </div>

      <div className="bg-[#0E0F11] border border-white/5 p-6 rounded-xl">
        <div className="space-y-6">
          {/* AI Configuration */}
          <div>
            <h3 className="text-lg font-medium tracking-tight text-white mb-4 flex items-center">
              <Settings className="w-5 h-5 mr-2" />
              Configuración de IA
            </h3>
            <div className="space-y-4">
              <div>
                <label className="text-xs font-bold uppercase tracking-[0.2em] text-[#A1A1AA] mb-2 block">
                  Proveedor de IA
                </label>
                <select
                  value={config.ai_provider}
                  onChange={(e) => setConfig({ ...config, ai_provider: e.target.value })}
                  className="w-full bg-[#050505] border border-white/10 text-white rounded-md px-3 py-2 focus:border-white/30 focus:ring-1 focus:ring-white/30 transition-all"
                >
                  <option value="openai">OpenAI (GPT-5.4, GPT-5.2)</option>
                  <option value="anthropic">Anthropic (Claude Sonnet 4.6)</option>
                  <option value="gemini">Google (Gemini 3.1 Pro)</option>
                </select>
              </div>

              <div>
                <label className="text-xs font-bold uppercase tracking-[0.2em] text-[#A1A1AA] mb-2 block">
                  Modelo (opcional - usar default del proveedor)
                </label>
                <input
                  type="text"
                  value={config.ai_model}
                  onChange={(e) => setConfig({ ...config, ai_model: e.target.value })}
                  placeholder="gpt-5.4, claude-sonnet-4-6, gemini-3.1-pro-preview"
                  className="w-full bg-[#050505] border border-white/10 text-white placeholder:text-zinc-600 rounded-md px-3 py-2 focus:border-white/30 focus:ring-1 focus:ring-white/30 transition-all"
                />
              </div>
            </div>
          </div>

          <div className="h-px bg-white/5" />

          {/* Project Configuration */}
          <div>
            <h3 className="text-lg font-medium tracking-tight text-white mb-4">
              Configuración del Proyecto
            </h3>
            <div className="space-y-4">
              <div>
                <label className="text-xs font-bold uppercase tracking-[0.2em] text-[#A1A1AA] mb-2 block">
                  Framework
                </label>
                <input
                  type="text"
                  value={config.framework}
                  onChange={(e) => setConfig({ ...config, framework: e.target.value })}
                  placeholder="React, Vue, Next.js, FastAPI, Django..."
                  className="w-full bg-[#050505] border border-white/10 text-white placeholder:text-zinc-600 rounded-md px-3 py-2 focus:border-white/30 focus:ring-1 focus:ring-white/30 transition-all"
                />
              </div>

              <div>
                <label className="text-xs font-bold uppercase tracking-[0.2em] text-[#A1A1AA] mb-2 block">
                  Lenguaje
                </label>
                <input
                  type="text"
                  value={config.language}
                  onChange={(e) => setConfig({ ...config, language: e.target.value })}
                  placeholder="TypeScript, Python, JavaScript..."
                  className="w-full bg-[#050505] border border-white/10 text-white placeholder:text-zinc-600 rounded-md px-3 py-2 focus:border-white/30 focus:ring-1 focus:ring-white/30 transition-all"
                />
              </div>

              <div>
                <label className="text-xs font-bold uppercase tracking-[0.2em] text-[#A1A1AA] mb-2 block">
                  Target de Despliegue
                </label>
                <input
                  type="text"
                  value={config.deployment_target}
                  onChange={(e) => setConfig({ ...config, deployment_target: e.target.value })}
                  placeholder="Vercel, AWS, Railway, Netlify..."
                  className="w-full bg-[#050505] border border-white/10 text-white placeholder:text-zinc-600 rounded-md px-3 py-2 focus:border-white/30 focus:ring-1 focus:ring-white/30 transition-all"
                />
              </div>
            </div>
          </div>

          <button
            onClick={handleSave}
            disabled={saving}
            className="w-full bg-white text-black font-medium hover:bg-neutral-200 transition-colors rounded-md py-6 mt-6 flex items-center justify-center"
          >
            {saving ? (
              <span className="flex items-center justify-center">
                <span className="spinner mr-2" />
                Guardando...
              </span>
            ) : (
              <>
                <Save className="w-4 h-4 mr-2" />
                Guardar Configuración
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConfigPanel;
